import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { authConfig } from '@app/modules/auth/config';
import { AuthSessionDeleter } from '@app/modules/auth/services/auth-user-session-deleter.service';
import { TokenChecker } from '@app/modules/auth/services/token-checker.service';
import { UserResponseDTO } from '@app/modules/user/interfaces/user-response-dto.interface';
import { Token } from '@interfaces/core/token.interface';
import { LocalStorageHandler } from '@services/local-storage-handler.service';
import { TranslationsFormatter } from '@shared/formatters/translations-formatter';
import { firstValueFrom } from 'rxjs';

import { AsyncTranslationsFinderFetcher } from '../fetchers/async-translations-finder-fetcher';
import { ApplicationTranslation } from '../interfaces/application-translation.interface';
import { ApplicationTranslationsSignalHandler } from '../signal-handlers/application-translations-signal-handler';

@Injectable()
export class ApplicationTranslationsFinder {
  constructor(
    private readonly asyncTranslationsFinderFetcher: AsyncTranslationsFinderFetcher,
    private readonly localStorageTokenHandler: LocalStorageHandler<Token>,
    private readonly localStorageUserHandler: LocalStorageHandler<UserResponseDTO>,
    private readonly translationsFormatter: TranslationsFormatter,
    private readonly applicationTranslationsSignalHandler: ApplicationTranslationsSignalHandler,
    private readonly http: HttpClient,
    private readonly sessionDeleter: AuthSessionDeleter,
    private readonly tokenChecker: TokenChecker,
  ) {}

  async run(): Promise<void> {
    const token = this.localStorageTokenHandler.read(
      authConfig.actions.userAuthentication.sections.authToken,
    );
    const user = this.localStorageUserHandler.read(
      authConfig.actions.userAuthentication.sections.authUser,
    );
    if (!token || !user) {
      await this.findTranslationsInLocal();

      return;
    }

    if (!this.tokenChecker.isTokenActive()) {
      await this.findTranslationsInLocal();
      this.sessionDeleter.run();

      return;
    }
    await this.findTranslationsInApi(token, user);
  }

  private async findTranslationsInApi(token: Token, user: UserResponseDTO) {
    const accessToken = token.accessToken;

    const language = user.defaultLanguage ?? 'es-es';
    const response = await this.asyncTranslationsFinderFetcher.run(
      accessToken,
      language,
    );
    if (!response.body) {
      return;
    }
    const translations = this.translationsFormatter.run(response.body);

    this.applicationTranslationsSignalHandler.setTranslations(translations);
  }

  private async findTranslationsInLocal(language?: string) {
    const languages = this.filterLanguages(navigator.languages);
    const languageToUse = language ?? languages[0];
    const response = await firstValueFrom(
      this.http.get(`assets/i18n/${languageToUse.toLocaleLowerCase()}.json`),
    );

    if (!response) {
      return;
    }
    this.applicationTranslationsSignalHandler.setTranslations(
      response as unknown as ApplicationTranslation,
    );
  }

  private filterLanguages(languages: readonly string[]) {
    return languages.filter((language) => language.includes('-'));
  }
}
